import { AfterViewInit, Component, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ColumnComponent, GridComponent } from '@progress/kendo-angular-grid';
import { Observable, forkJoin, of } from 'rxjs';
import { AssetModelSettingsService } from '~/services/modules/assets/assetModelSettings';
import * as XLSX from 'xlsx';
import { FileToImport, ImportObject, ModelColumns } from '~/front-models/fileToImport';
import { DataWithPagingMetadata } from '~/infrastructure/paging';
import { AccountSite } from '~/models/administration/accountSite';
import { UserSummary } from '~/models/administration/user';
import { CustomFieldSummary } from '~/models/modules/customField';
import { AccountsService } from '~/services/administration.ts/accounts';
import { CustomFieldsService } from '~/services/modules/stocks/custom-fields';
import { GridFieldsService } from '~/services/modules/stocks/gridField';
import { SecurityService } from '~/services/security.service';
import { ThemeManagerService } from '~/services/theme-manager';
import { importFileMapOptions } from './import-file-map';
import { SharedService } from '~/services/sharedService';
import { ImportService } from '~/services/modules/stocks/import';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { SitesEnum } from '~/shared/enums/sites.enum';

@Component({
    selector: 'app-import',
    templateUrl: './import.html',
    styleUrls: ['./import.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class ImportComponent implements AfterViewInit, OnInit {
  page: string = 'stock-import';
  title: string = 'Import';
  showTextBox = false;

  _localpermissions: any;
  _currentUser: UserSummary;
  _currentBox: number = 0;
  _fileMapList = importFileMapOptions;
  _fileMapKeys: string[] = [];
  _currentStep = 0;
  _fileImported: File;
  _fileSelected = new FormControl();
  _fileSheetSelected = new FormControl();
  _modelName: string;
  _labelName: string;
  _sheetIndex: number;
  _sheetNames: string[];
  _arrayBuffer: any;
  _dataGrid: any;
  _canGoNext: boolean;
  _theme: string;
  loading = true;
  uploadSaveUrl = 'saveUrl';
  uploadRemoveUrl = 'removeUrl';
  _steps = [
    { label: 'Select File', text: 'Step 1' },
    { label: 'Import Info', text: 'Step 2', disabled: true },
    { label: 'Map Fields', text: 'Step 3', disabled: true },
    { label: 'Validating ', text: 'Step 4', disabled: true },
    { label: 'Import', text: 'Step 5', disabled: true },
  ];
  _enableSiteSelection: boolean = true;
  _showAccountsGrid: boolean = true;

  fileExcelData: any;
  importObject: ImportObject;
  selectedModel: FileToImport;
  importAccounts: AccountSite[] = [];
  _customFields: CustomFieldSummary[];
  _language = 'en';
  mappedFields: any = [];
  ImportMapList: any = [];
  _gridColumns: any[] = [];

  AssignAssetFields: any = [];
  _hasMappingLicense: boolean = true;
  @ViewChild('importValidationComponent') _importValidationComponent: any;

  @ViewChild('grid') grid: GridComponent;

  accessFrom: string;
  isAsset: boolean = false;
  ExcludedUserColumns = ['Profile'];
  ExcludedLocationColumns = ['account.id'];
  ExcludedPersonColumns = ['active', 'isUser', 'profileImage.thumbnail', 'user.appRole.name', 'user.email'
    , 'user.hasComplexPassword', 'user.isMobileUser', 'user.timezone.name', 'user.twoFactorEnabled', 'user.userName'];
  ExcludedStockItemColumns = ['inventoryUnitOfMeasure.unitName', 'issueUnitOfMeasure.unitName', 'receiveUnitOfMeasure.unitName', 'receiveUnitOfMeasure.multiplier'];
  ExcludedSalesOrderColumns = ['soAttachedDocument', 'stockSalesOrderDetails.stockItem.description', 'stockSalesOrderDetails.stockItem.issueUnitOfMeasure.code',
    'stockSalesOrderDetails.extendedPrice', 'stockSalesOrderDetails.unitsIssued', 'customer.note'];
  ExcludedPurchaseOrderColumns = ['poAttachedDocument']
  assetsModules: string[] = ['departments', 'Statuses', 'Conditions', 'AssetTypes', 'Models', 'Asset', 'Funding Source', 'asset-purchaseOrders']
  ExcludedAssetColumns = ['model.assetType.description', 'model.assetType.modelType'
    , 'model.modelInfo.additionalDescription', 'model.modelInfo.category.code',
    'model.modelInfo.category.description', 'model.modelInfo.modelManufacturer', 'model.name', 'checkoutDueDays', 'checkoutDueDate', 'createdOn', 'model.modelFiles.file.thumbnail', 'purchaseOrderDate']
  //ExcludedAssetModelColumns: string[] = ['site.code'];
  ExcludedAssetModelColumns: string[] = [];
  showSaveMap: boolean = false;
  selectedMap: any;
  // #region Multi-Site
  _isMultiSite: boolean = false;
  _isMultiSiteSelected: boolean = false;
  // #endregion


  constructor(private route: ActivatedRoute,
    private router: Router,
    private _accountsService: AccountsService,
    private _themeManager: ThemeManagerService,
    private _customFieldsService: CustomFieldsService,
    private _securityService: SecurityService,
    private _GridFieldsService: GridFieldsService,
    private _importService: ImportService,
    private _layoutComponent: LayoutComponent,
    public _sharedService: SharedService,
    private _assetModelSettingsService: AssetModelSettingsService) {

    this.route.data.subscribe(data => {
      this.accessFrom = data.accessFrom;
    });
    this._currentUser = this._securityService.currentUser;
    this._fileMapKeys = [];
    this._securityService.getLicense().subscribe((lic: any) => {
      const hasPurchaseOrderStock = !!lic?.stock?.purchaseOrder;
      const hasSalesOrderStock = !!lic?.stock?.salesOrder;
      const hasPurchaseOrderAsset = !!lic?.asset?.purchaseOrder;

      const orderedKeys = [
        'assets',
        'statuses',
        'fundingsource',
        'asset-models',
        'asset-types',
        'categories',
        'conditions',
        'departments',
        'locations',
        'locationTypes',
        'addresses',
        'persons',
        'vendors',
        'customers',
        'purchaseOrders',
        'asset-purchaseOrders',
        'facility',
        'uom',
        'stockItems',
        'salesOrders',
        'pickList',
        'inventories',
        'building',
        'floor',
        'disposeTo',
      ];
      for (const key of orderedKeys) {

        if (Object.prototype.hasOwnProperty.call(importFileMapOptions, key)) {
          switch (key) {
            case 'pickList':
              this.canAccess('pickList', 'create') && this.accessFrom === 'stocks' && !this._currentUser.isEnterprise ? this._fileMapKeys.push(key) : null;
              // this.canAccess('pickList', 'create') && this.accessFrom === 'stocks' ? this._fileMapKeys.push(key) : null;

              break;
            case 'stockItems':
              this.canAccess('stockItems', 'create') && this.accessFrom === 'stocks' ? this._fileMapKeys.push(key) : null;

              break;
            // case 'users': // may be needed

            // this.canAccess('users', 'create') && (this.accessFrom === 'assets' || this.accessFrom === "stocks") && !this._currentUser.isEnterprise ? this._fileMapKeys.push(key) : null;

            // break;
            case 'persons':

              this.canAccess('persons', 'create') && (this.accessFrom === 'assets' || this.accessFrom === "stocks") && !this._currentUser.isEnterprise ? this._fileMapKeys.push(key) : null;
              break;
            case 'categories':
              this.canAccess('categories', 'create') && (this.accessFrom === 'assets' || this.accessFrom === 'stocks') ? this._fileMapKeys.push(key) : null;

              break;
            case 'customers':
              this.canAccess('customers', 'create') && this.accessFrom === 'stocks' ? this._fileMapKeys.push(key) : null;

              break;
            case 'uom':
              this.canAccess('uom', 'create') && this.accessFrom === 'stocks' ? this._fileMapKeys.push(key) : null;

              break;
            case 'vendors':
              this.canAccess('vendors', 'create') && (this.accessFrom === 'assets' || this.accessFrom === 'stocks') ? this._fileMapKeys.push(key) : null;

              break;
            case 'addresses':
              this.canAccess('addresses', 'create') && (this.accessFrom === 'assets' || this.accessFrom === 'stocks') ? this._fileMapKeys.push(key) : null;

              break;
            case 'salesOrders':
              this.canAccess('salesOrders', 'create') && this.accessFrom === 'stocks' && !this._currentUser.isEnterprise && hasSalesOrderStock ? this._fileMapKeys.push(key) : null;

              break;
            case 'purchaseOrders':
              this.canAccess('purchaseOrders', 'create') && this.accessFrom === 'stocks' && !this._currentUser.isEnterprise && hasPurchaseOrderStock ? this._fileMapKeys.push(key) : null;

              break;
            case 'locations':
              this.canAccess('locations', 'create') && (this.accessFrom === 'assets' || this.accessFrom === 'stocks') ? this._fileMapKeys.push(key) : null;

              break;
            case 'locationTypes':
              this.canAccess('locationTypes', 'create') && (this.accessFrom === 'assets' || this.accessFrom === 'stocks') ? this._fileMapKeys.push(key) : null;

              break;

            // case 'facility':
            // this.canAccess('facility', 'create') && this.accessFrom === 'stocks' ? this._fileMapKeys.push(key) : null;

            // break;

            case 'building':
              this.canAccess('building', 'create') && this.accessFrom === 'stocks' && !this._currentUser.isEnterprise ? this._fileMapKeys.push(key) : null;
              break;

            case 'floor':
              this.canAccess('floor', 'create') && this.accessFrom === 'stocks' && !this._currentUser.isEnterprise ? this._fileMapKeys.push(key) : null;
              break;

            case 'disposeTo':
              this.canAccess('disposeTo', 'create') && (this.accessFrom === 'assets' || this.accessFrom === 'stocks') && !this._currentUser.isEnterprise ? this._fileMapKeys.push(key) : null;

              break;
            case 'inventories':
              this.canAccess("inventories", "read") &&
                this.accessFrom === 'stocks' && !this._currentUser.isEnterprise ? this._fileMapKeys.push(key) : null;

              break;
            case 'departments':
              this.canAccess('departments', 'create') && (this.accessFrom === 'assets' || this.accessFrom === "stocks") ? this._fileMapKeys.push(key) : null;

              break;
            case 'statuses':
              this.canAccess('statuses', 'create') && this.accessFrom === 'assets' ? this._fileMapKeys.push(key) : null;

              break;
            case 'conditions':
              this.canAccess('conditions', 'create') && this.accessFrom === 'assets' ? this._fileMapKeys.push(key) : null;

              break;
            case 'asset-types':
              this.canAccess('asset-types', 'create') && this.accessFrom === 'assets' ? this._fileMapKeys.push(key) : null;

              break;
            case 'fundingsource':
              this.canAccess('fundingsource', 'create') && this.accessFrom === 'assets' ? this._fileMapKeys.push(key) : null;

              break;
            case 'asset-models':
              this.canAccess('asset-models', 'create') && this.accessFrom === 'assets' ? this._fileMapKeys.push(key) : null;
              break;
            case 'assets':
              this.canAccess('assets', 'create') && this.accessFrom === 'assets' ? this._fileMapKeys.push(key) : null;
              break;
            case 'asset-purchaseOrders':
              this.canAccess('asset-purchaseOrders', 'create') &&
                !this._currentUser.isEnterprise &&
                this.accessFrom === 'assets' && hasPurchaseOrderAsset ? this._fileMapKeys.push(key) : null;

              break;
            case 'facility':
              this.canAccess('facility', 'create') &&
                this.accessFrom === 'stocks' && !this._currentUser.isEnterprise ? this._fileMapKeys.push(key) : null;

              break;
          }
        }
      }
    });

    this._themeManager.themeChange.subscribe((data) => {
      this._theme = data;
    });

    // select file to import
    let fileSelected = this.route.snapshot.queryParams['file'] || '';

    if (fileSelected === 'categories' ||
      fileSelected === 'uom' ||
      fileSelected === 'addresses' ||
      fileSelected === 'asset-types' ||
      fileSelected === 'conditions' ||
      fileSelected === 'fundingsource' ||
      fileSelected === 'statuses' ||
      fileSelected === 'locationTypes' ||
      fileSelected === 'departments') {
      this._enableSiteSelection = false;
      this._modelName = fileSelected;
    }
    else {
      this._enableSiteSelection = true;
      this._modelName = fileSelected;
    }

    this._fileSelected.valueChanges.subscribe((x) => {
      this.importAccounts = [];
      this._showAccountsGrid = false;
      setTimeout(() => {
        this._showAccountsGrid = true;
      });

      this._getAccounts();

      if (['categories', 'uom', 'addresses', 'asset-types', 'conditions', 'fundingsource', 'statuses', 'locationTypes', 'departments'].includes(x)) {
        this._enableSiteSelection = false;
      }
      else {
        this._enableSiteSelection = true;
      }
      this._modelName = importFileMapOptions[x]?.modelName;

      // this.updateSelectedModel(importFileMapOptions[this._fileSelected.value]);

      this.setCanGoToStepTwo();

      if (this._modelName && this._currentBox === 1) {
        this._currentBox = 2;
      }
    });
    //

    this._currentUser = this._securityService.currentUser;

    if (fileSelected && importFileMapOptions[fileSelected]) {
      this._fileSelected.setValue(fileSelected);
    }

    // select sheet
    this._fileSheetSelected.valueChanges.subscribe((x) => {
      this._sheetIndex = Number(x);

      if (this._fileImported) {
        this._loadData(this._fileImported);
      }
    });
    this._fileSheetSelected.setValue(0);
    //

    this.importObject = this.initImportObject();
  }

  //#region  ngOnInit
  closeTextbox(event: any) {
    //this.title=event.value;
    this._GridFieldsService.EditPageTitle(this.page, event.value).subscribe((data: any) => {
      this.title = data.titleName;
      this._sharedService.TitleChanged.emit(true);
    });
    this.showTextBox = false;
  }
  showTextbox() {
    this.showTextBox = true;
    //document.getElementById('titleTextBox').focus();
    //this.titleTextBox.nativeElement.focus();
  }

  ngOnInit() {
    this._checkMultiSitesLicense();
    this.loading = false;
    let url = window.location.href;
    this._GridFieldsService.getPageByName(this.page).subscribe((data) => {
      if (data.titleName) {
        this.title = data.titleName;
      }
    });

    // this._GridFieldsService.EditPageTitle(this.page, "event.value").subscribe((data: any) => {
    //   this.title = data.titleName;

    //   this._sharedService.TitleChanged.emit(true);
    // });

    //this.isAsset = this._sharedService.base == "asset" ? true : false;
    this.isAsset = this.accessFrom == "assets" ? true : false;

    if (url.includes('file=')) {
      this._modelName = importFileMapOptions[url.split('file=')[1]]?.modelName;
      this._labelName = importFileMapOptions[url.split('file=')[1]]?.labelName;
      this.GetGridFieldForImport(url.split('file=')[1]);

      if (!this._currentUser.isEnterprise) {
        this._next();

      }
    }

    // select file to import
    let fileSelected = this.route.snapshot.queryParams['file'] || '';
    if (!fileSelected && !this._fileSelected.value) {
      this._currentBox = 1;
    } else {
      if (this._currentUser.isEnterprise) {
        this._currentBox = 2;
      }
      else {
        this.setCanGoToStepTwo();
      }
    }
  }

  //#endregion
  ngAfterViewInit() {
  }
  _getAccounts = (params?: any, model?: any): Observable<DataWithPagingMetadata<any>> => {
    return this._accountsService.getAccounts(params, model);
  }

  CopySelectedModelsColumns: any = [];

  updateSelectedModel(selectedModel: FileToImport, FromStep1: boolean = false) {
    selectedModel.labelName = selectedModel?.labelName?.trim();

    if (this._customFields == undefined)
      this._customFields = [];
    //if (selectedModel.labelName == 'stockItems' && this._customFields == null || (this._customFields != null && this._customFields.length == 0)) {
    if (FromStep1 == true) {

      if (selectedModel.labelName != 'departments') {
        var customFieldEntity = this.accessFrom == "assets" && !this._sharedService.
          commonModulesHasStockController.includes(selectedModel.labelName) ? "asset" : "stock"
        this.loadCustomFields(customFieldEntity, selectedModel.labelName, selectedModel);
      }
      else
        this.loadCustomFields('common', selectedModel.labelName, selectedModel);
    }
    else {
      this.selectedModel = selectedModel;
    }
  }

  updateMappedFields(mappedFields: any) {
    Promise.resolve().then(() => {
      this.mappedFields = mappedFields;
    });
  }

  async loadCustomFields(entity: any, page: any, selectedModel: FileToImport) {
    this._customFieldsService.getCustomFields(entity, page).subscribe((data) => {
      this._customFields = data;

      for (const column of this._customFields) {
        selectedModel.columns.push({
          label: column.name,
          name: column.name,
          field: column.name,
          type:
            column.customFieldType.type == "String" || column.customFieldType.type == "URL" ? "text"
              : column.customFieldType.type == "Numeric" ? "numeric"
                : column.customFieldType.type == "Date" ? "date"
                  : "text"
          ,
          mappedName: column.name,
          mappedNameToValidate: null,
          isCustomField: true,
          required: column.isRequired,
          entity: 'customFieldsValue'
        });
      }
      this.selectedModel = selectedModel;
      this.CopySelectedModelsColumns = [...selectedModel.columns];
    });
  }

  updateImportObject(dataToImport: any) {
    this.importObject.dataToImport = dataToImport;
  }

  setCanGoNext(canGo: boolean) {
    setTimeout(() => {
      this._canGoNext = canGo;

      if (canGo) {
        this._steps[this._currentStep + 1]['disabled'] = false;
      } else {
        this._steps[this._currentStep + 1]['disabled'] = true;
      }
    });
  }

  disableImportStep() {
    this._steps[this._steps.length - 1]['disabled'] = true;
  }

  _back() {
    this._currentStep--;
    // if (this._currentStep === 0) {
    //   this.resetSteps();
    //   this.setCanGoToStepTwo();
    // }
    this._currentStepChange();
  }

  _next() {
    //this._modelName = this._modelName  ?? 'assets';
    // if (this.assetsModules.find(f => f.toLowerCase() == this._labelName.toLowerCase())) {
    //   this.isAsset = true;
    // }


    this._currentStep++;
    this._currentStepChange();

    if (this._currentStep == this._steps.length - 1) {
      for (let i = 1; i < this._steps.length - 1; i++) {
        this._steps[i]['disabled'] = true;
      }
    }
  }

  saveMap() {
    if (!this.selectedMap) {
      this._layoutComponent.openPopup({
        title: 'Validation',
        message: 'Please Fill Map Name',
        withoutConfirm: true,
      });

      this.showSaveMap = false;

      return false;
    }

    let importMappDetailModel: any[] = [];

    this.mappedFields.forEach((element: any) => {
      let mapDetailObject = {
        importMapId: 0,
        fieldName: element.barcloudField,
        gridFieldText: element.barcloudMappedName,
        excelName: element.importedField,
      }

      importMappDetailModel.push(mapDetailObject);
    });


    let itemSelected = { mapName: this.selectedMap, ImportMappDetailUpdateModel: importMappDetailModel };
    this._importService.createImportMap(itemSelected).subscribe((data: Array<any>) => {
      this.selectedMap = null;
      this.showSaveMap = false;
      this._importService.isMapSaved.emit(true);
    });

  }

  _closeSaveMap() {
    this.showSaveMap = false;
  }

  _openSaveMap() {
    this.showSaveMap = true;
  }

  _close() {
    this.accessFrom === "assets" ? this.router.navigate(['asset/']) : this.router.navigate(['stock']);

  }

  _currentStepChange() {
    if (this._currentStep === 0) {
      this.resetSteps();
    }

    if (this._currentStep !== this._steps.length - 1) {
      this._canGoNext = !this._steps[this._currentStep + 1]['disabled'];
    }

    if (this._currentStep === 1) {
      setTimeout(() => {
        this._currentBox = 3;
      }, 1000);
    }
  }

  // _saveMap() { }

  _reValidate() {
    this._importValidationComponent.validateData();
  }

  _fileSelectedChanges(file: File) {
    this._fileImported = file;
    this._loadData(this._fileImported);
  }

  _loadData(file: File) {
    const fileReader = new FileReader();

    fileReader.onload = (e) => {
      this._arrayBuffer = fileReader.result;
      const data = new Uint8Array(this._arrayBuffer);
      const arr = new Array();

      for (let i = 0; i !== data.length; ++i) {
        arr[i] = String.fromCharCode(data[i]);
      }

      const bstr = arr.join('');
      const workbook = XLSX.read(bstr, { type: 'binary', cellDates: true, dateNF: 'dd-MMM-yy' });

      this._sheetNames = workbook.SheetNames;
      const firstSheetName = workbook.SheetNames[this._sheetIndex];
      const worksheet = workbook.Sheets[firstSheetName];

      // skip the first row
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      // let C, R = range.s.r; /* start in the first row */
      worksheet['!ref'] = XLSX.utils.encode_range(range);

      this.fileExcelData = XLSX.utils.sheet_to_json(worksheet, { raw: true, defval: '' });
      //this.fileExcelData = XLSX.utils.sheet_to_json(worksheet);
      if (this.fileExcelData && this.fileExcelData.length) {
        this.setCanGoNext(true);
      }
      else {
        this.setCanGoNext(false);
      }

      this._dataGrid = this.fileExcelData.slice(0, 5);

      if (this._dataGrid && this._dataGrid.length > 0) {
        this._gridColumns = Object.keys(this._dataGrid[0]);
        if (this.grid) {
          const columns = this._gridColumns.map(f => {
            const column = new ColumnComponent();
            column.field = f;
            return column;
          });
          this.grid.columns.reset(columns);
        }
      } else {
        this._gridColumns = [];
      }

      if (this.grid) {
        this.grid.data = this._dataGrid;
      }
    };

    fileReader.readAsArrayBuffer(file);
  }

  _onAccountRowSelectionChanges(e: AccountSite[]) {
    if (this._currentUser.isEnterprise) {
      this.setCanGoNext(false);
      this.importAccounts = [];

      if (this._enableSiteSelection) {
        e = e.filter((item, i, arr) => arr.findIndex(t => t.accountId === item.accountId) === i);
        e.forEach((item) => {
          this.importAccounts.push({
            siteId: this._enableSiteSelection ? item.siteId : null,
            accountId: this._enableSiteSelection ? +item.accountId : +e
          });
        });
      }
      else {
        e = e.filter((item, i, arr) => arr.findIndex(t => t === item) === i);
        this.importAccounts = e;
      }

      if (this._currentUser.isEnterprise && this.importAccounts.length === 0) {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please select account(s) to continue.'
        });

        return false;
      }

      this.setCanGoToStepTwo();
    }
  }

  private setCanGoToStepTwo() {
    if (this._currentUser.isEnterprise) {
      if (this._modelName && this.importAccounts.length > 0) {
        if (this._enableSiteSelection) {
          var tmpAccountSites = this.importAccounts.filter((x: any) => x.siteId != -1);

          if (tmpAccountSites.length == this.importAccounts.length) {
            this.setCanGoNext(true);
          }
          else {
            this.setCanGoNext(false);
          }
        }
        else {
          this.setCanGoNext(true);
        }

      }
      else {
        this.setCanGoNext(false);
      }
    }
    else {
      this.setCanGoNext(true);
    }
  }

  private resetSteps() {
    this._sheetNames = [];
    this._sheetIndex = 0;
    this._dataGrid = [];
    this._gridColumns = [];
    this.fileExcelData = [];
    this.importAccounts = [];
    this.importObject = this.initImportObject();
    this._customFields = [];
    this._fileSelected = new FormControl();

    for (let i = 1; i < this._steps.length; i++) {
      this._steps[i]['disabled'] = true;
    }

    if (this.selectedModel)
      this.selectedModel.columns.forEach((x) => {
        x.mappedName = '';
      });

    this.mappedFields = [];
  }

  // stockLocationMinMaxFields: any = [] ,
  private initImportObject() {
    return {
      dataToImport: [] as any[],
      accountSite: {
        accountId: 0,
        siteId: 0,
      },
      accountSites: [] as AccountSite[],
      accountIds: [] as any[],
      importUpsertSetting: "new" as string,
      MappedExcelColumns: [] as any,
    };
  }

  canAccess(scope: string, operation: string): boolean {
    //var test = this._securityService.hasPermission(null, `${scope}-${operation}`);
    return this._securityService.hasPermission(null, `${scope}-${operation}`);
  }

  StockLocationMinMaxFields: any = [];

  GetGridFieldForImport(page: string, language?: string) {
    // this.router.navigate([], {
    //   queryParams: {
    //     'yourParamName': null,
    //     'youCanRemoveMultiple': null,
    //   },
    //   queryParamsHandling: 'merge'
    // })

    const fields$ = this._GridFieldsService.GetMapFieldsForImportByPage(page, this._language);
    const settings$ = page === 'assets'
      ? this._assetModelSettingsService.getassetModelSettings()
      : of(null);

    forkJoin({ fields: fields$, settings: settings$ }).subscribe(({ fields, settings }) => {
      if (fields) {
        if (page == "persons") {
          fields.columns = fields.columns.filter(x => !this.ExcludedPersonColumns.includes(x.field));

          if (!this._isMultiSiteSelected) {
            fields.columns = fields.columns.filter(x => x.field != 'userSites.site');
          }
          else {
            var multiSiteField: any = fields.columns.find(x => x.field == 'userSites.site');
            multiSiteField.required = true;
          }
        }

        if (page == "users") {
          fields.columns = fields.columns.filter(x => !this.ExcludedUserColumns.includes(x.field))
        }

        if (page == "locations") {
          fields.columns = fields.columns.filter(x => !this.ExcludedLocationColumns.includes(x.field))
        }

        this._modelName = importFileMapOptions[fields.modelName]?.modelName;
        this._labelName = importFileMapOptions[fields.modelName]?.labelName;

        if (page == "stockItems") {
          fields.columns = fields.columns.filter(x => !this.ExcludedStockItemColumns.includes(x.field))
          this._GridFieldsService.getByPageForImportStockItemLocationMinMax(page, this._language).subscribe((data: any) => {

            this.StockLocationMinMaxFields = data.columns;
            this.StockLocationMinMaxFields.forEach((element: any) => {
              element.name = element.name;
              element.entityRelation = "OneToMany";
              element.type =
                element.type?.toString().toLocaleLowerCase() == "text" || element.type?.toString().toLocaleLowerCase() == "string" || element.type?.toString().toLocaleLowerCase() == "url" ? "text"
                  : element.type?.toString().toLocaleLowerCase() == "numeric" || element.type?.toString().toLocaleLowerCase() == "number" ? "numeric"
                    : element.type?.toString().toLocaleLowerCase() == "date" ? "date"
                      : element.type?.toString().toLocaleLowerCase() == "boolean" ? "boolean"
                        : "text"

            });
          });
        }

        if (page == "salesOrders") {
          fields.columns = fields.columns.filter(x => !this.ExcludedSalesOrderColumns.includes(x.field))
        }

        if (page == "purchaseOrders") {
          fields.columns = fields.columns.filter(x => !this.ExcludedPurchaseOrderColumns.includes(x.field))
        }

        if (page == "assets") {
          fields.columns = fields.columns.filter(x => !this.ExcludedAssetColumns.includes(x.field));
          const isModelRequired = settings ? settings.isRequired : false;
          fields.columns.forEach((column: any) => {
            if (column.field == 'model.number' || column.field == 'modelCode') {
              column.required = isModelRequired;
            }
          });
          this._GridFieldsService.getByPageForImportAssignAsset(page, this._language).subscribe((data: any) => {
            this.AssignAssetFields = data.columns;
            this.AssignAssetFields.forEach((element: any) => {
              element.name = element.name;
              //element.entityRelation = "OneToOnw";
              element.type =
                element.type?.toString().toLocaleLowerCase() == "string" || element.type?.toString().toLocaleLowerCase() == "text" || element.type?.toString().toLocaleLowerCase() == "url" ? "text"
                  : element.type?.toString().toLocaleLowerCase() == "numeric" || element.type?.toString().toLocaleLowerCase() == "number" ? "numeric"
                    : element.type?.toString().toLocaleLowerCase() == "date" ? "date"
                      : element.type?.toString().toLocaleLowerCase() == "boolean" ? "boolean"
                        : "text"
              element.required = true;

            });
          });
        }

        if (page == "asset-models") {
          fields.columns = fields.columns.filter(x => !this.ExcludedAssetModelColumns.includes(x.field))
        }

        //var MainFields = [...fields];
        if (this._currentUser.isEnterprise) // Enterprise
        {
          fields.columns = fields.columns.filter((x: ModelColumns) => x.field != "site.code" && x.field != "site.name");
        }
        else // NG
        {
          if (!this._isMultiSiteSelected) {
            fields.columns = fields.columns.filter((x: ModelColumns) => x.field != "site.code" && x.field != "site.name");
          }
        }

        let staticData: any = importFileMapOptions[fields.modelName]?.columns;
        staticData = staticData ?? [];

        fields.columns.forEach(element => {
          element.type =
            element.type?.toString().toLocaleLowerCase() == "string" || element.type?.toString().toLocaleLowerCase() == "url" || element.type?.toString().toLocaleLowerCase() == "text" ? "text"
              : element.type?.toString().toLocaleLowerCase() == "numeric" || element.type?.toString().toLocaleLowerCase() == "number" ? "numeric"
                : element.type?.toString().toLocaleLowerCase() == "date" ? "date"
                  : element.type?.toString().toLocaleLowerCase() == "boolean" ? "boolean"
                    : "text"
          if (this._modelName) {
            var CheckMappedColumn = staticData.find((x: any) => x.label.toLowerCase().trim() == element.label.toLowerCase().trim());
          }

          if (CheckMappedColumn) {
            element.name = CheckMappedColumn.name;
            element.fieldName = CheckMappedColumn.fieldName;
            element.entity = CheckMappedColumn.entity;
            element.entityRelation = CheckMappedColumn.entityRelation;
            element.type = CheckMappedColumn.type == "string" ? "text" : CheckMappedColumn.type;
            element.relatedDbField = CheckMappedColumn.relatedDbField;
          }

          if (!this._currentUser.isEnterprise && this._isMultiSiteSelected && element.field == "site.code")
            element.required = true;
        });

        if (page == "stockItems") {
          var masterStockNumberField = staticData.find((x: any) => x.label == "master stock number");

          if (masterStockNumberField) {
            masterStockNumberField.field = 'code';
            masterStockNumberField.type = 'text';
            masterStockNumberField.mappedName = 'Master Stock Number';
            masterStockNumberField.mappedNameToValidate = '';
            fields.columns.push(staticData.find((x: any) => x.label == "master stock number"));
          }

          if (this._currentUser.isEnterprise) {
            fields.columns = fields.columns.filter(x => x.label != "Default Location Id")
          }
        }

        if (!fields.labelName) {
          fields.labelName = fields.modelName;
        }

        if (fields.labelName == 'Category') {
          fields.labelName = 'Categories';
        }

        this._canGoNext = true;
        this.setCanGoToStepTwo();
        fields.modelName = this._modelName;
        this.CopySelectedModelsColumns = [...fields.columns];
        this.updateSelectedModel(fields, true);
      }
    });
  }

  _checkMultiSitesLicense() {
    this._securityService.getLicense().subscribe((data: any) => {
      if (data.general.numberOfSites > 1) {
        this._isMultiSite = true;
      }

      if (this._isMultiSite == true) {
        var userSites = JSON.parse(localStorage.getItem('loggedInAccountSites'));
        let selectedSite = userSites[0].siteId;

        if (selectedSite == null || selectedSite == SitesEnum.AllSites) {
          this._isMultiSiteSelected = true;
        }
      }

      if (data && data.general && data.general.mapping === false) {
        this._hasMappingLicense = false;
        this._steps = this._steps.filter(x => x.label !== 'Map Fields');
      }
    });
  }
}
